import React, { useEffect, useMemo, useRef, useState } from "react";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function isSolved(order) {
  return order.every((piece, slot) => piece === slot);
}

function shuffledOrder(total) {
  let order = shuffle([...Array(total).keys()]);
  // Derangement: no piece starts already correct (avoids 1–2 tiles showing as solved).
  if (total > 1 && order.some((piece, slot) => piece === slot)) {
    return shuffledOrder(total);
  }
  return order;
}

/**
 * In-box jigsaw. All pieces start shuffled inside the grid (grayscale while
 * misplaced). The player swaps tiles - by mouse drag or by tap-to-select then
 * tap-to-swap (touch friendly). A tile that lands in its correct slot turns to
 * full color and locks. When every tile is correct, `onComplete` fires.
 */
export default function JigsawBoard({ src, rows = 3, cols = 3, onComplete }) {
  const total = rows * cols;
  const [order, setOrder] = useState(() => shuffledOrder(total));
  const [selected, setSelected] = useState(null);
  const [dragFrom, setDragFrom] = useState(null);
  const [overSlot, setOverSlot] = useState(null);
  const completedRef = useRef(false);

  useEffect(() => {
    setOrder(shuffledOrder(total));
    setSelected(null);
    setDragFrom(null);
    setOverSlot(null);
    completedRef.current = false;
  }, [total, src]);

  const solved = useMemo(() => isSolved(order), [order]);

  useEffect(() => {
    if (!completedRef.current && total > 0 && solved) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [solved, total, onComplete]);

  const locked = (slot) => order[slot] === slot;

  const swap = (a, b) => {
    if (a == null || b == null || a === b) return;
    if (locked(a) || locked(b)) return; // correct tiles stay put
    setOrder((prev) => {
      const next = [...prev];
      [next[a], next[b]] = [next[b], next[a]];
      return next;
    });
  };

  const handleTap = (slot) => {
    if (locked(slot)) return;
    if (selected == null) {
      setSelected(slot);
    } else if (selected === slot) {
      setSelected(null);
    } else {
      swap(selected, slot);
      setSelected(null);
    }
  };

  // Touch dragging (HTML5 DnD doesn't fire on touchscreens). Track the tile
  // under the finger via elementFromPoint and swap on release.
  const touchMovedRef = useRef(false);

  const slotFromPoint = (x, y) => {
    const el = document.elementFromPoint(x, y);
    const cell = el && el.closest ? el.closest("[data-slot]") : null;
    if (!cell) return null;
    const n = Number(cell.getAttribute("data-slot"));
    return Number.isInteger(n) ? n : null;
  };

  const handleTouchStart = (slot) => {
    if (locked(slot)) return;
    touchMovedRef.current = false;
    setDragFrom(slot);
  };

  const handleTouchMove = (e) => {
    if (dragFrom == null) return;
    touchMovedRef.current = true;
    const t = e.touches[0];
    if (!t) return;
    const slot = slotFromPoint(t.clientX, t.clientY);
    setOverSlot(slot);
  };

  const handleTouchEnd = (e, slot) => {
    // Suppress the synthetic click that would otherwise re-trigger handleTap.
    if (e.cancelable) e.preventDefault();
    if (touchMovedRef.current && dragFrom != null && overSlot != null) {
      swap(dragFrom, overSlot);
      setSelected(null);
    } else {
      // A tap (no real drag) falls back to tap-to-select/swap.
      handleTap(slot);
    }
    setDragFrom(null);
    setOverSlot(null);
    touchMovedRef.current = false;
  };

  const pieceStyle = (pieceId, gray) => {
    const pr = Math.floor(pieceId / cols);
    const pc = pieceId % cols;
    return {
      backgroundImage: `url(${src})`,
      backgroundSize: `${cols * 100}% ${rows * 100}%`,
      backgroundPosition: `${cols > 1 ? (pc / (cols - 1)) * 100 : 0}% ${
        rows > 1 ? (pr / (rows - 1)) * 100 : 0
      }%`,
      ...(gray ? { filter: "grayscale(1) brightness(0.9)" } : null),
    };
  };

  return (
    <div className="tg-jigsaw-wrap">
      <div
        className={`tg-jigsaw-grid${solved ? " tg-jigsaw-grid--solved" : ""}`}
        style={{
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, 1fr)`,
          aspectRatio: `${cols} / ${rows}`,
        }}
      >
        {order.map((pieceId, slot) => {
          const correct = pieceId === slot;
          return (
            <div
              key={slot}
              data-slot={slot}
              className={`tg-jigsaw-cell${selected === slot ? " tg-jigsaw-cell--selected" : ""}${
                overSlot === slot ? " tg-jigsaw-cell--over" : ""
              }${correct ? " tg-jigsaw-cell--correct" : ""}`}
              draggable={!correct}
              onClick={() => handleTap(slot)}
              onDragStart={() => setDragFrom(slot)}
              onDragEnd={() => {
                setDragFrom(null);
                setOverSlot(null);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (overSlot !== slot) setOverSlot(slot);
              }}
              onDragLeave={() => setOverSlot((prev) => (prev === slot ? null : prev))}
              onDrop={() => {
                swap(dragFrom, slot);
                setDragFrom(null);
                setOverSlot(null);
                setSelected(null);
              }}
              onTouchStart={() => handleTouchStart(slot)}
              onTouchMove={handleTouchMove}
              onTouchEnd={(e) => handleTouchEnd(e, slot)}
            >
              <div
                className={`tg-jigsaw-piece${correct ? " tg-jigsaw-piece--correct" : ""}`}
                style={pieceStyle(pieceId, !correct)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
